import styled from "@emotion/styled/macro";
import { ReviewOffer } from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import {
  consumptionCardData as consumptionCardDataFixture,
  productionCardData as productionCardDataFixture,
  systemSizeCardData as systemSizeCardDataFixture,
  storageCapacityCardData as storageCapacityCardDataFixture,
  maxPowerCardData as maxPowerCardDataFixture,
  backupTimeCardData as backupTimeCardDataCardDataFixture,
} from "../../fixtures/SystemCarouselData";

import usageOffsetImg from "../assets/images/carousel/usage-offset-img.png";
import { formatNumber } from "utils/formatHelpers";
import { isBigTipText } from "utils/textUtils";
import { FLEX_MONTHLY } from "constants/financialProducts";

const tipText =
  "This is an estimate and does not guarantee actual offset. The system design may change based on a detailed engineering site survey. Actual offset will vary based on the final system size, design, configuration, system location, local climate, and your family's energy usage.";

const additionalSystemOffsetDisclaimer =
  "*This is only accounting for their true up bill annual leftover usage";

const getInfoToolTipTextLines = (
  production: number | undefined,
  consumption: number | undefined,
  usageOffset: number | undefined,
  additionalSystem?: boolean
) => {
  if (!production && !consumption && !usageOffset) {
    return {
      singleLineText: "",
      lines: "",
    };
  }

  const lines = [
    `${formatNumber(
      Math.round(Number(production))
    )} Expected Solar Production (kWh)\xA0/`,
    `${formatNumber(
      Math.round(Number(consumption))
    )} Estimated usage (kWh) from manual entry`,
    `= ${formatNumber(Math.round(Number(usageOffset)))}% Usage Offset`,
    tipText,
    ...(additionalSystem ? [additionalSystemOffsetDisclaimer] : []),
  ];

  const singleLineText = lines.join(" ");

  return {
    singleLineText,
    lines: lines.map((textLine, index) =>
      textLine === tipText || textLine === additionalSystemOffsetDisclaimer ? (
        <SpacedTextLineSpan key={index}>{textLine}</SpacedTextLineSpan>
      ) : (
        <TextLineSpan key={index}>{textLine}</TextLineSpan>
      )
    ),
  };
};

const getUtilityEnergyToolTipTextLines = () => ({
  singleLineText: "",
  lines: (
    <Box>
      <div>
        <b>Est. Energy from the utility %</b> is the estimated percent of{" "}
        <b>current usage</b> that will continue to be supplied by the utility.
        Actual <b>energy from the utility</b> will vary based on your usage
        patterns in the future
      </div>
      <div>
        This estimate will be finalized for you to approve after engineering
        review, and may be adjusted based on the final system design.
      </div>
    </Box>
  ),
});

const getSelfSupplyToolTipTextLines = () => ({
  singleLineText: "",
  lines: (
    <Box>
      <div>
        <b>Self Supply %</b> represents the estimated percent of current usage
        that will be supplied by the Sunrun system. Actual energy from Sunrun
        will vary based on your usage patterns in the future.
      </div>
      <div>
        This estimate will be finalized for you to approve after engineering
        review, and may be adjusted based on the final system design.
      </div>
    </Box>
  ),
});

const systemSizeCardData = (offer: ReviewOffer | undefined) => {
  let data = {
    ...systemSizeCardDataFixture,
    value: offer?.systemSize,
  };

  if (offer?.financial?.summary?.type === FLEX_MONTHLY) {
    data.extendedDescription = `Including ${offer?.financial.financePlan}`;
  }

  return data;
};

const productionCardData = (offer: ReviewOffer | undefined) => {
  let data = {
    ...productionCardDataFixture,
    value: offer?.production,
  };

  if (offer?.financial?.summary?.type === FLEX_MONTHLY) {
    data.value = offer.production;
    data.extendedDescription = `Including ${offer?.financial.financePlan}`;
  }

  return data;
};

const usageOffsetCardData = (offer: ReviewOffer | undefined) => {
  const { production, consumption, usageOffset, additionalSystem } =
    offer ?? {};
  const usageDataTooltip = getInfoToolTipTextLines(
    production,
    consumption,
    usageOffset,
    additionalSystem
  );

  let data = {
    headerText: "Estimated annual usage offset",
    infoToolTipText: usageDataTooltip.lines,
    footerTextSuffix: "%",
    iconMedia: usageOffsetImg,
    extendedDescription: "",
    isBigTip: isBigTipText(usageDataTooltip.singleLineText),
    value: usageOffset,
  };

  if (offer?.financial?.summary?.type === FLEX_MONTHLY) {
    data.extendedDescription = `Including ${offer?.financial.financePlan}`;
  }

  return data;
};

const energyOffsetCardData = (offer: ReviewOffer | undefined) => {
  const { usageOffset } = offer ?? {};
  const utilityEnergyTooltip = getUtilityEnergyToolTipTextLines();

  let data = {
    headerText: "Est. energy offset",
    subText: "not the whole story",
    infoToolTipText: utilityEnergyTooltip.lines,
    footerTextSuffix: "%",
    iconMedia: usageOffsetImg,
    extendedDescription: "",
    isBigTip: isBigTipText(utilityEnergyTooltip.singleLineText),
    value: usageOffset,
  };

  if (offer?.financial?.summary?.type === FLEX_MONTHLY) {
    data.extendedDescription = `Including ${offer?.financial.financePlan}`;
  }

  return data;
};

const selfSupplyCardData = (offer: ReviewOffer | undefined) => {
  const { annualSelfSupplyPercent } = offer ?? {};
  const selfSupplyTooltip = getSelfSupplyToolTipTextLines();

  let data = {
    headerText: "Est. self supply",
    infoToolTipText: selfSupplyTooltip.lines,
    footerTextSuffix: "%",
    iconMedia: usageOffsetImg,
    extendedDescription: "",
    isBigTip: isBigTipText(selfSupplyTooltip.singleLineText),
    value: annualSelfSupplyPercent,
  };

  if (offer?.financial?.summary?.type === FLEX_MONTHLY) {
    data.extendedDescription = `Including ${offer?.financial.financePlan}`;
  }

  return data;
};

const consumptionCardData = (offer: ReviewOffer | undefined) => {
  let data = { ...consumptionCardDataFixture, value: offer?.consumption };
  if (offer?.financial?.summary?.type === FLEX_MONTHLY) {
    data.extendedDescription = "Current annual usage";
  }
  return data;
};

const currentUsageCardData = (offer: ReviewOffer | undefined) => {
  let data = {
    ...consumptionCardDataFixture,
    headerText: "Est. current usage",
    value: offer?.consumption,
  };

  if (offer?.financial?.summary?.type === FLEX_MONTHLY) {
    data.extendedDescription = `Including ${offer?.financial.financePlan}`;
  }

  return data;
};

const storageCapacityCardData = (offer: ReviewOffer | undefined) => {
  let data = {
    ...storageCapacityCardDataFixture,
    value: offer?.usableCapacityKwh,
    roundToOneDecimal: true,
  };

  return data;
};

const maxPowerCardData = (offer: ReviewOffer | undefined) => {
  let data = {
    ...maxPowerCardDataFixture,
    value: offer?.maxPowerKW,
    roundToOneDecimal: true,
  };

  return data;
};

const backupTimeCardData = (offer: ReviewOffer | undefined) => {
  let data = {
    ...backupTimeCardDataCardDataFixture,
    value: "Included*",
    isBigTip: true,
  };

  return data;
};

const TextLineSpan = styled.span`
  display: block;
`;

const SpacedTextLineSpan = styled(TextLineSpan)`
  margin-top: 1em;
`;

const Box = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export {
  getSelfSupplyToolTipTextLines,
  usageOffsetCardData,
  energyOffsetCardData,
  selfSupplyCardData,
  systemSizeCardData,
  consumptionCardData,
  currentUsageCardData,
  productionCardData,
  storageCapacityCardData,
  maxPowerCardData,
  backupTimeCardData,
};
